import { useEffect, useRef, useState } from "react";

// Muted, looping background video with its first frame as the poster. If the
// browser refuses to autoplay (e.g. iOS Low Power Mode), it swaps to the
// poster image for this page load — same fallback pattern as the template's
// Portal/Hero loops.
export const LoopVideo = ({
  src,
  poster,
  alt = "",
  className = "",
  style,
  testId,
  videoRef,
  // While true, only the poster image shows and the video isn't requested,
  // so something more important (the desktop dive) can download first.
  defer = false,
}) => {
  const ref = useRef(null);
  const [blocked, setBlocked] = useState(false);
  // The video stays invisible until it has actually painted a frame, then
  // fades in over the poster, so the swap never hard-cuts.
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (videoRef) videoRef.current = v;
    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(() => setShown(true));
      else setShown(true);
    };
    v.addEventListener("playing", show);
    v.play().catch(() => setBlocked(true));
    return () => v.removeEventListener("playing", show);
  }, [videoRef, defer]);

  // The poster is always the same mounted <img> (never remounted when the
  // video arrives), laid under the video: iOS Safari ignores
  // object-fit/object-position on a <video> poster. Callers always pass
  // absolute full-bleed classes, so the two stack exactly.
  return (
    <>
      <img
        src={poster}
        alt={blocked || defer ? alt : ""}
        aria-hidden={blocked || defer ? undefined : true}
        data-testid={blocked || defer ? testId : undefined}
        className={className}
        style={style}
      />
      {!blocked && !defer && (
        <video
          ref={ref}
          data-testid={testId}
          className={className}
          style={{ ...style, opacity: shown ? 1 : 0, transition: "opacity 1.2s ease", willChange: "opacity" }}
          src={src}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          tabIndex={-1}
          aria-label={alt || undefined}
        />
      )}
    </>
  );
};
